import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogArticles } from "@/mocks/blog";

const categories = ["Tous", "Toiture-terrasse", "Parking", "Isolation thermique", "Diagnostic", "Terrasse", "Réglementation"];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("Tous");

  const featured = blogArticles.find((a) => a.featured);
  const filtered = blogArticles.filter((a) =>
    activeCategory === "Tous" ? true : a.category === activeCategory
  );
  const others = filtered.filter((a) => !a.featured || activeCategory !== "Tous");

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-[#f5fdf8] overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#00a340]"></div>
          <div className="absolute bottom-0 right-20 w-96 h-96 rounded-full bg-[#00a340]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#00a340] mb-3">Ressources & Conseils</span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Le Blog <span className="text-[#00a340]">KS BTP</span>
          </h1>
          <p className="text-gray-500 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed">
            Conseils d&apos;experts, guides techniques et actualités du secteur BTP — tout ce qu&apos;il faut savoir sur l&apos;étanchéité, l&apos;isolation et la rénovation de bâtiments.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">

        {/* Featured article */}
        {featured && activeCategory === "Tous" && (
          <Link
            to={`/blog/${featured.slug}`}
            className="group block rounded-2xl overflow-hidden border border-gray-100 hover:border-[#a8f0c0] transition-all mb-10 md:mb-14"
          >
            <div className="flex flex-col lg:flex-row">
              <div className="relative w-full lg:w-1/2 h-56 md:h-72 lg:h-auto flex-shrink-0">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 rounded-full bg-[#00a340] text-white text-xs font-bold uppercase tracking-wider">
                    À la une
                  </span>
                </div>
              </div>
              <div className="flex flex-col justify-center p-6 md:p-10 lg:w-1/2">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${featured.categoryColor}`}>{featured.category}</span>
                  <span className="text-gray-400 text-xs">{featured.date}</span>
                  <span className="text-gray-400 text-xs flex items-center gap-1">
                    <i className="ri-time-line"></i> {featured.readTime}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-3 leading-tight group-hover:text-[#00a340] transition-colors" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {featured.title}
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{featured.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00a340] text-white text-xs font-black flex-shrink-0">EV</div>
                    <div>
                      <div className="text-xs font-bold text-gray-800">{featured.author}</div>
                      <div className="text-xs text-gray-400">{featured.authorRole}</div>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[#00a340] text-sm font-bold group-hover:gap-2.5 transition-all whitespace-nowrap">
                    Lire l&apos;article <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8 md:mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-all ${
                activeCategory === cat
                  ? "bg-[#00a340] text-white"
                  : "bg-[#f5fdf8] text-gray-600 hover:bg-[#e6f7ed] hover:text-[#00a340]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Articles grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {others.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="group flex flex-col rounded-2xl overflow-hidden border border-gray-100 hover:border-[#a8f0c0] transition-all bg-white"
            >
              <div className="relative h-44 overflow-hidden flex-shrink-0">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${article.categoryColor}`}>{article.category}</span>
                </div>
              </div>
              <div className="flex flex-col flex-1 p-5">
                <div className="flex items-center gap-3 mb-2.5 text-gray-400 text-xs">
                  <span>{article.date}</span>
                  <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                </div>
                <h3 className="font-extrabold text-gray-900 text-sm md:text-base leading-snug mb-2 group-hover:text-[#00a340] transition-colors" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {article.title}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed flex-1 mb-4">{article.excerpt}</p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00a340] text-white text-[9px] font-black flex-shrink-0">EV</div>
                    <span className="text-xs text-gray-500">{article.author}</span>
                  </div>
                  <span className="text-[#00a340] text-xs font-bold flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                    Lire <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA devis */}
        <div className="mt-16 md:mt-20 rounded-2xl bg-[#0d2b18] p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl md:text-2xl font-extrabold text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Un projet d&apos;étanchéité en tête ?
            </h3>
            <p className="text-gray-400 text-sm">Nos experts vous répondent sous 48h avec un devis gratuit et sans engagement.</p>
          </div>
          <Link
            to="/devis"
            className="flex-shrink-0 px-7 py-3.5 rounded-md bg-[#00a340] text-white font-bold text-sm whitespace-nowrap hover:bg-[#007a30] transition-colors cursor-pointer"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
